import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import ContentStudioWorkoutsStore from "stores/ContentStudioWorkoutsStore";

import ConfirmationDialog from "components/ConfirmationDialog";
import Page from "components/Layout/Page";
import SortingControl from "components/SortingControl";
import FilteringControl from "components/FilteringControl";
import Placeholder from "components/Placeholder";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import NavBar from "../NavBar";
import WorkoutCard from "./WorkoutCard";

import { SNACKBAR_TYPES } from "components/Snackbar";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { TableSortType } from "stores/templates/TableStore";

import { getConfirmArchivationText } from "utils/publish";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  WorkoutCardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Workouts extends React.Component<Props> {
  @computed get workouts() {
    return ContentStudioWorkoutsStore.items;
  }

  @computed get workoutListIsEmpty() {
    return this.workouts.length === 0;
  }

  componentDidMount() {
    ContentStudioWorkoutsStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioWorkoutsStore.resetPage();
  }

  fetchMoreWorkouts = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioWorkoutsStore.fetchMoreWorkouts();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentStudioWorkoutsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentStudioWorkoutsStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioWorkoutsStore.clearSearch();
    ContentStudioWorkoutsStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioWorkoutsStore.applyOrder(value);
    ContentStudioWorkoutsStore.resetPage();
    ContentStudioWorkoutsStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentStudioWorkoutsStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentStudioWorkoutsStore.resetPage();
    ContentStudioWorkoutsStore.fetch();
  };

  onFilterClear = () => {
    ContentStudioWorkoutsStore.clearFilters();
    ContentStudioWorkoutsStore.fetch();
  };

  onAddWorkout = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (workout: any) => () => {
    const path = generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
      workoutId: workout.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (workout: any) => () => {
    const path = generatePath(ROUTES.CONTENT_STUDIO_WORKOUT_DETAILS, {
      workoutId: workout.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (workout: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = workout;

    try {
      await ContentStudioWorkoutsStore.changeWorkoutPublishStatus(
        id,
        publishStatus,
      );
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onArchive = (workout: any) => () => {
    const { openPopup } = this.props;

    const workoutId = workout.id;

    const text = getConfirmArchivationText({
      entity: workout,
      suffix: "workout",
    });

    openPopup({
      key: "ArchiveWorkout",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Workout"}
          text={text}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={ContentStudioWorkoutsStore.isWorkoutManaging}
          onDecline={close}
          onAccept={this.archiveWorkout(workoutId, close)}
        />
      ),
    });
  };

  archiveWorkout = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioWorkoutsStore.archiveWorkout(id);
      close();
      ContentStudioWorkoutsStore.resetPage();
      ContentStudioWorkoutsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onCopy = (workout: any) => async () => {
    const { setMessage } = this.props;
    const id = workout.id;

    try {
      await ContentStudioWorkoutsStore.copyWorkout(id);
      ContentStudioWorkoutsStore.resetPage();
      ContentStudioWorkoutsStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Workouts"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <RestoreArchivedButton />
            </PageControl>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddWorkout}
              >
                Add Workout
              </Button>
            </PageControl>
          </>
        }
        isLoading={ContentStudioWorkoutsStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={ContentStudioWorkoutsStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={
                  ContentStudioWorkoutsStore.orderContext.appliedOrderInfo
                }
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={ContentStudioWorkoutsStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(ContentStudioWorkoutsStore.isWorkoutPublishing ||
          ContentStudioWorkoutsStore.isWorkoutCopying) && <OverflowLoader />}
        {!this.workoutListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.workouts.length}
            hasMore={ContentStudioWorkoutsStore.hasMoreInfo}
            next={this.fetchMoreWorkouts}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.workouts.map(workout => (
              <WorkoutCardWrapper key={workout.id}>
                <WorkoutCard
                  title={workout.title}
                  duration={workout.durationText}
                  image={workout.imageUrl}
                  publishStatus={workout.publishStatus}
                  onDetailsClick={this.onDetailsClick(workout)}
                  onArchive={this.onArchive(workout)}
                  onEdit={this.onEdit(workout)}
                  onCopy={this.onCopy(workout)}
                  onPublish={this.onPublish(workout)}
                />
              </WorkoutCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              ContentStudioWorkoutsStore.search
                ? "No matches found. Please try another search."
                : "No workouts added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Workouts)));
